import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CONTACT_WARNING,
  ContactComplianceRefusal,
  DNC_BLOCKED,
  DNC_CHECK_QUEUED,
  LITIGATOR_BLOCKED,
} from "@/model/interface/Compliance";
import { DncCheckMode } from "@/enum/DncCheckMode";

interface ContactComplianceDialogProps {
  refusal: ContactComplianceRefusal | null;
  actionLabel: string;
  onChooseCheck: (mode: DncCheckMode) => void;
  onAcknowledge: (suppressFromNowOn: boolean) => void;
  onCancel: () => void;
}

const TITLES: Record<string, string> = {
  [CONTACT_WARNING]: "No consent on file",
  [DNC_CHECK_QUEUED]: "Check in progress",
  [DNC_BLOCKED]: "Number blocked",
  [LITIGATOR_BLOCKED]: "Number blocked",
};

export default function ContactComplianceDialog({
  refusal,
  actionLabel,
  onChooseCheck,
  onAcknowledge,
  onCancel,
}: ContactComplianceDialogProps) {
  const [suppressFromNowOn, setSuppressFromNowOn] = useState(false);
  const isWarning = refusal?.code === CONTACT_WARNING;
  const isInformational = refusal?.code === DNC_CHECK_QUEUED;

  const formatPrice = (price: number) => `$${price.toFixed(2)}`;

  useEffect(() => {
    if (refusal) {
      setSuppressFromNowOn(false);
    }
  }, [refusal]);

  return (
    <AlertDialog
      open={refusal !== null}
      onOpenChange={(value) => {
        if (!value) {
          onCancel();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{refusal ? TITLES[refusal.code] ?? "Compliance check" : ""}</AlertDialogTitle>
          <AlertDialogDescription>{refusal?.detail}</AlertDialogDescription>
        </AlertDialogHeader>

        {refusal && refusal.phoneNumbers.length > 1 && (
          <ul className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm tabular-nums">
            {refusal.phoneNumbers.map((phoneNumber) => (
              <li key={phoneNumber}>{phoneNumber}</li>
            ))}
          </ul>
        )}

        {isWarning && refusal && (refusal.instantPrice !== undefined || refusal.delayedPrice !== undefined) && (
          <div className="grid gap-2">
            {refusal.instantPrice !== undefined && (
              <Button onClick={() => onChooseCheck(DncCheckMode.INSTANT)}>
                Check instantly ({formatPrice(refusal.instantPrice)})
              </Button>
            )}
            {refusal.delayedPrice !== undefined && (
              <Button variant="outline" onClick={() => onChooseCheck(DncCheckMode.DELAYED)}>
                Check in a few minutes ({formatPrice(refusal.delayedPrice)})
              </Button>
            )}
          </div>
        )}

        {isWarning && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              id="suppress-contact-warning"
              checked={suppressFromNowOn}
              onCheckedChange={(value) => setSuppressFromNowOn(value === true)}
            />
            <span>Don't warn me again</span>
          </label>
        )}

        <AlertDialogFooter>
          {refusal?.learnMoreUrl && (
            <Button variant="link" asChild className="mr-auto px-0">
              <a href={refusal.learnMoreUrl} target="_blank" rel="noreferrer">
                Learn more
              </a>
            </Button>
          )}
          <AlertDialogCancel onClick={onCancel}>
            {isInformational ? "OK" : isWarning ? "Cancel" : "Close"}
          </AlertDialogCancel>
          {isWarning && (
            <Button variant="destructive" onClick={() => onAcknowledge(suppressFromNowOn)}>
              {actionLabel}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
